/**
 * Firebase Storage 서비스
 * 파일 업로드 및 관리
 */

import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  getMetadata,
  getBlob 
} from 'firebase/storage';
import { storage } from './firebase';

/**
 * 보안 메모 파일 업로드
 * @param {File} file - 업로드할 파일
 * @param {string} userId - 사용자 ID
 * @param {string} noteId - 메모 ID (옵션)
 * @returns {Promise<Object>} 파일 정보 객체
 */
export async function uploadNoteFile(file, userId, noteId = null) {
  try {
    // 파일 이름 생성 (보안을 위해 랜덤 ID 사용)
    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const fileExtension = file.name.split('.').pop();
    const fileName = `${fileId}.${fileExtension}`;
    
    // 저장 경로 생성
    const filePath = noteId 
      ? `users/${userId}/notes/${noteId}/${fileName}`
      : `users/${userId}/temp/${fileName}`;
    
    // Storage 참조 생성
    const storageRef = ref(storage, filePath);
    
    // 메타데이터 설정 (한글 인코딩을 위해)
    const metadata = {
      contentType: file.type,
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString()
      }
    };
    
    // 텍스트 파일의 경우 UTF-8 인코딩 명시
    if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
      metadata.contentType = 'text/plain; charset=utf-8';
    }
    
    // 파일 업로드
    const snapshot = await uploadBytes(storageRef, file, metadata);
    
    // 다운로드 URL 획득
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    // 파일 정보 반환
    return {
      id: fileId,
      name: file.name,
      type: file.type,
      size: file.size,
      url: downloadURL,
      path: filePath,
      uploadedAt: new Date()
    };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error(`파일 업로드 실패: ${error.message}`);
  }
}

/**
 * 여러 파일 업로드
 * @param {File[]} files - 업로드할 파일 배열
 * @param {string} userId - 사용자 ID
 * @param {string} noteId - 메모 ID (옵션)
 * @returns {Promise<Object[]>} 파일 정보 배열
 */
export async function uploadNoteFiles(files, userId, noteId = null) {
  try {
    const uploadPromises = files.map(file => 
      uploadNoteFile(file, userId, noteId)
    );
    
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error uploading multiple files:', error);
    throw new Error(`파일 업로드 실패: ${error.message}`);
  }
}

/**
 * 파일 삭제
 * @param {string} filePath - 삭제할 파일 경로
 * @returns {Promise<boolean>} 삭제 성공 여부
 */
export async function deleteNoteFile(filePath) {
  try {
    const storageRef = ref(storage, filePath);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    // 파일이 존재하지 않는 경우는 성공으로 처리
    if (error.code === 'storage/object-not-found') {
      return true;
    }
    console.error('Error deleting file:', error);
    throw new Error(`파일 삭제 실패: ${error.message}`);
  }
}

/**
 * 여러 파일 삭제
 * @param {string[]} filePaths - 삭제할 파일 경로 배열
 * @returns {Promise<boolean>} 삭제 성공 여부
 */
export async function deleteNoteFiles(filePaths) {
  try {
    const deletePromises = filePaths.map(path => deleteNoteFile(path));
    await Promise.all(deletePromises);
    return true;
  } catch (error) {
    console.error('Error deleting multiple files:', error);
    return false;
  }
}

/**
 * 파일 메타데이터 조회
 * @param {string} filePath - 파일 경로
 * @returns {Promise<Object>} 파일 메타데이터
 */
export async function getFileMetadata(filePath) {
  try {
    const storageRef = ref(storage, filePath);
    const metadata = await getMetadata(storageRef);
    return metadata;
  } catch (error) {
    console.error('Error getting file metadata:', error);
    throw new Error(`파일 정보 조회 실패: ${error.message}`);
  }
}

/**
 * 임시 파일을 메모 파일로 이동
 * @param {string} tempPath - 임시 파일 경로
 * @param {string} userId - 사용자 ID
 * @param {string} noteId - 메모 ID
 * @returns {Promise<string>} 새 파일 경로
 */
export async function moveToNoteFiles(tempPath, userId, noteId) {
  try {
    // 임시 파일에서 다운로드 URL 획득
    const tempRef = ref(storage, tempPath);
    const downloadURL = await getDownloadURL(tempRef);
    
    // 파일명 추출
    const fileName = tempPath.split('/').pop();
    const newPath = `users/${userId}/notes/${noteId}/${fileName}`;
    
    // 새 위치에 파일 복사 (실제로는 URL은 동일하지만 논리적 경로만 변경)
    const newRef = ref(storage, newPath);
    
    // 메타데이터를 사용하여 파일 재업로드 (Firebase Storage는 이동 기능이 제한적)
    const response = await fetch(downloadURL);
    const blob = await response.blob();
    await uploadBytes(newRef, blob);
    
    // 임시 파일 삭제
    await deleteNoteFile(tempPath);
    
    return newPath;
  } catch (error) {
    console.error('Error moving file:', error);
    throw new Error(`파일 이동 실패: ${error.message}`);
  }
}

/**
 * 파일 크기 제한 검증
 * @param {File} file - 검증할 파일
 * @param {number} maxSize - 최대 크기 (바이트)
 * @returns {boolean} 유효성 여부
 */
export function validateFileSize(file, maxSize = 10485760) { // 10MB
  return file.size <= maxSize;
}

/**
 * 파일 타입 검증
 * @param {File} file - 검증할 파일
 * @param {string[]} allowedTypes - 허용된 타입 배열
 * @returns {boolean} 유효성 여부
 */
export function validateFileType(file, allowedTypes = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf', 
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/zip', 'application/x-zip-compressed'
]) {
  return allowedTypes.includes(file.type);
}

/**
 * 파일 유효성 종합 검증
 * @param {File} file - 검증할 파일
 * @param {Object} options - 검증 옵션
 * @returns {Object} 검증 결과
 */
export function validateFile(file, options = {}) {
  const {
    maxSize = 10485760, // 10MB
    allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf', 
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/zip', 'application/x-zip-compressed'
    ]
  } = options;

  const errors = [];

  if (!validateFileSize(file, maxSize)) {
    errors.push(`파일 크기가 ${(maxSize / 1048576).toFixed(1)}MB를 초과합니다.`);
  }

  if (!validateFileType(file, allowedTypes)) {
    errors.push('지원되지 않는 파일 형식입니다.');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Firebase Storage에서 파일 내용 가져오기 (인증된 요청)
 * @param {string} filePath - Storage 파일 경로
 * @returns {Promise<Blob>} 파일 Blob
 */
export async function getFileBlob(filePath) {
  try {
    const storageRef = ref(storage, filePath);
    const blob = await getBlob(storageRef);
    return blob;
  } catch (error) {
    console.error('Error getting file blob:', error);
    throw new Error(`파일 읽기 실패: ${error.message}`);
  }
}

/**
 * Firebase Storage에서 파일 텍스트 가져오기 (인증된 요청)
 * @param {string} filePath - Storage 파일 경로
 * @returns {Promise<string>} 파일 텍스트 내용
 */
export async function getFileText(filePath) {
  try {
    const blob = await getFileBlob(filePath);
    const text = await blob.text();
    return text;
  } catch (error) {
    console.error('Error getting file text:', error);
    throw new Error(`파일 텍스트 읽기 실패: ${error.message}`);
  }
}